import {useCallback, useEffect, useState} from "react";
import {AlertTriangle, LayoutGrid} from "lucide-react";
import {createAdminCmsHomeBox, deleteAdminCmsHomeBox, listAdminCmsHomeBoxes, updateAdminCmsHomeBox, uploadAdminMedia, type AdminCmsHomeBox} from "@zaaaz/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs, AdminRowTrailing} from "./ui";

const emptyForm = {nume: "", url: "", ordine: 0, visible: true, zona: 0, imagine: ""};

export function CmsHomeBoxesView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("continut");
  const [items, setItems] = useState<AdminCmsHomeBox[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminCmsHomeBoxes({page, pageSize: 20}).then((r) => { setItems(r.items); setTotalPages(r.totalPages); }).catch((e: Error) => setErr(e.message));
  }, [page]);
  useEffect(() => { load(); }, [load]);
  async function onUpload(id: number, file: File) {
    const r = await uploadAdminMedia({folder: "images/casute/", table: "pagini_casute", column: "imagine", id, file});
    setForm((f) => ({...f, imagine: r.filename}));
    load();
  }
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(b: AdminCmsHomeBox) {
    setOpenId(b.id);
    setForm({nume: b.nume, url: b.url, ordine: b.ordine, visible: b.visible, zona: b.zona, imagine: b.imagine});
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          await createAdminCmsHomeBox(form);
          adminToast.success("Box added.");
        } else if(typeof openId === "number") {
          await updateAdminCmsHomeBox(openId, form);
          adminToast.success("Box saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete box?", async () => {
      await deleteAdminCmsHomeBox(id);
      closeModal(false);
      load();
      adminToast.success("Box deleted.");
    });
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.continut.title}
      description="Content boxes on the homepage"
      breadcrumbs={[
        {label: ADMIN_SECTION_NAV.continut.title, href: "/vadmin/continut"},
        {label: "Boxes"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add box" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="continut" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(b) => b.id}
        getTitle={(b) => b.nume}
        getSubtitle={(b) => b.url}
        getMeta={(b) => (
          <>
            {!b.visible ? <AdminBadge variant="danger"><AlertTriangle className="h-3 w-3" /></AdminBadge> : null}
            <AdminBadge variant="secondary">Ord. {b.ordine}</AdminBadge>
            <AdminBadge variant="outline">{b.zonaLabel}</AdminBadge>
          </>
        )}
        getSearchText={(b) => adminSearchText(b.id, b.nume, b.url, b.zonaLabel, b.ordine, b.imagine)}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No boxes"
        emptyDescription="Add a new box."
        searchPlaceholder="Search box, URL, zona..."
        getRowActions={(b) => (
          <AdminRowTrailing onDelete={() => removeItem(b.id)} deleteLabel="Delete box" />
        )}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New box" : "Edit box"}
        icon={LayoutGrid}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          <AdminField label="Name">
            <Input value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} />
          </AdminField>
          <AdminField label="URL">
            <Input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} />
          </AdminField>
          <AdminField label="Order">
            <Input type="number" value={form.ordine} onChange={(e) => setForm({...form, ordine: Number(e.target.value)})} />
          </AdminField>
          <AdminField label="Zone">
            <Select value={String(form.zona)} onValueChange={(v) => setForm({...form, zona: Number(v)})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Below carousel</SelectItem>
                <SelectItem value="1">Below promotions</SelectItem>
              </SelectContent>
            </Select>
          </AdminField>
          {form.imagine ? (
            <div className="admin-detail-panel__section">
              <p><img src={`https://www.zaaaz.eu/images/casute/${form.imagine}`} alt="" /> {form.imagine}</p>
            </div>
          ) : null}
          {typeof openId === "number" ? (
            <AdminField label="Imagine">
              <Input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if(f) onUpload(openId, f); }} />
            </AdminField>
          ) : null}
        </div>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
